import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { IoArrowForwardOutline, IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import { challengeApi, claimApi, readerApi, reportApi } from '../../services/api';
import { parseReference, toChips } from '../../utils/parseReference';
import { pickReader, setLastReaderId } from '../../utils/localPreferences';
import { isClaimsUiEnabled } from '../../utils/claimsEnabled';
import LogMoment from './LogMoment';
import ClaimSlot from './ClaimSlot';
import './QuickLog.css';

/**
 * One-tap logging (WS2).
 *
 * Two ways in, both without leaving the page:
 *   1. "Continue" — the chapter after this reader's most recent report.
 *   2. "I read…"  — free text parsed into chapters, shown as chips first.
 *
 * Everything else (date, other readers, notes, the details form) stays behind
 * "Adjust", which links to the full reporting page.
 */
const QuickLog = ({ readers, userId, onLogged }) => {
  const [selectedReader, setSelectedReader] = useState(null);
  const [nextChapter, setNextChapter] = useState(null);
  const [loadingNext, setLoadingNext] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [showAdjust, setShowAdjust] = useState(false);
  const [moment, setMoment] = useState(null);
  const [momentLabel, setMomentLabel] = useState('');
  const [lastReportIds, setLastReportIds] = useState([]);
  const claimsOn = isClaimsUiEnabled();
  const [claimChallengeId, setClaimChallengeId] = useState(null);
  const [claimEnabled, setClaimEnabled] = useState(false);
  const [claimSuggestions, setClaimSuggestions] = useState([]);
  const [myClaim, setMyClaim] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);

  // Remembered reader first, then primary — never re-ask on every visit.
  useEffect(() => {
    if (readers.length > 0 && !selectedReader) {
      setSelectedReader(pickReader(readers, userId));
    }
  }, [readers, userId, selectedReader]);

  const loadNextChapter = useCallback(async (readerId) => {
    if (!readerId) return;
    try {
      setLoadingNext(true);
      const response = await readerApi.getNextChapter(readerId);
      setNextChapter(response.data.nextChapter);
    } catch (err) {
      console.error('Failed to load next chapter:', err);
      setNextChapter(null);
    } finally {
      setLoadingNext(false);
    }
  }, []);

  const loadSuggestions = useCallback(async (readerId, challengeId) => {
    if (!claimsOn || !readerId || !challengeId) return;
    try {
      setClaimsLoading(true);
      const response = await challengeApi.getSuggestions(challengeId, readerId);
      const data = response.data || {};
      setClaimEnabled(data.enabled === true);
      setClaimSuggestions(data.suggestions || []);
      setMyClaim(data.myClaim || null);
      setClaimError(null);
    } catch (err) {
      console.error('Failed to load chapter claims:', err);
      setClaimSuggestions([]);
      setMyClaim(null);
    } finally {
      setClaimsLoading(false);
    }
  }, [claimsOn]);

  useEffect(() => {
    if (selectedReader) {
      setLastReaderId(userId, selectedReader.id);
      loadNextChapter(selectedReader.id);
    }
  }, [selectedReader, userId, loadNextChapter]);

  useEffect(() => {
    if (!claimsOn) return undefined;

    let cancelled = false;
    challengeApi.getActive()
      .then(response => {
        if (cancelled) return;
        const challenge = response.data?.challenge;
        const apiOn = response.data?.claimsEnabled === true;
        setClaimChallengeId(challenge?.id || null);
        setClaimEnabled(Boolean(challenge?.id) && apiOn);
      })
      .catch(err => {
        console.error('Failed to load active challenge for claims:', err);
      });

    return () => { cancelled = true; };
  }, [claimsOn]);

  useEffect(() => {
    if (claimsOn && selectedReader && claimChallengeId && claimEnabled) {
      loadSuggestions(selectedReader.id, claimChallengeId);
    }
  }, [claimsOn, selectedReader, claimChallengeId, claimEnabled, loadSuggestions]);

  const parsed = useMemo(() => parseReference(text), [text]);
  const chips = useMemo(() => toChips(parsed.chapters), [parsed.chapters]);

  const submit = async (chapterIds, label) => {
    if (!selectedReader || chapterIds.length === 0) return;

    try {
      setSubmitting(true);
      setError(null);

      const response = await reportApi.createReport({
        readerId: selectedReader.id,
        chapterIds
      });

      setMoment(response.data.moment);
      setMomentLabel(label);
      setLastReportIds((response.data.reports || []).map(r => r.id));
      setText('');

      // Advance the Continue button and refresh the community map behind us.
      loadNextChapter(selectedReader.id);
      if (claimChallengeId) {
        loadSuggestions(selectedReader.id, claimChallengeId);
      }
      onLogged?.(response.data);
    } catch (err) {
      console.error('Failed to log reading:', err);
      setError(err.response?.data?.error || 'Could not log that. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (!nextChapter) return;
    submit([nextChapter.chapterId], `${nextChapter.book} ${nextChapter.chapter}`);
  };

  const handleClaim = async (suggestion) => {
    if (!selectedReader || !claimChallengeId) return;
    try {
      setClaiming(true);
      setClaimError(null);
      const response = await claimApi.create({
        readerId: selectedReader.id,
        chapterId: suggestion.chapterId,
        challengeId: claimChallengeId
      });
      setMyClaim(response.data.claim);
    } catch (err) {
      console.error('Failed to claim chapter:', err);
      setClaimError(err.response?.data?.error || 'Could not claim that chapter.');
      if (claimChallengeId) {
        loadSuggestions(selectedReader.id, claimChallengeId);
      }
    } finally {
      setClaiming(false);
    }
  };

  const handleRelease = async (claim) => {
    if (!claim?.id) return;
    try {
      setClaiming(true);
      setClaimError(null);
      await claimApi.release(claim.id);
      setMyClaim(null);
      if (selectedReader && claimChallengeId) {
        await loadSuggestions(selectedReader.id, claimChallengeId);
      }
    } catch (err) {
      console.error('Failed to release claim:', err);
      setClaimError(err.response?.data?.error || 'Could not release that claim.');
    } finally {
      setClaiming(false);
    }
  };

  const handleClaimLog = (chapter) => {
    if (!chapter?.chapterId) return;
    submit([chapter.chapterId], `${chapter.book} ${chapter.chapter}`);
  };

  const handleTextSubmit = (event) => {
    event.preventDefault();
    if (parsed.chapters.length === 0) return;
    const label = chips.map(c => c.label).join(', ');
    submit(parsed.chapters.map(c => c.chapterId), label);
  };

  const handleUndo = async () => {
    try {
      await reportApi.undoLastBatch();
      setMoment(null);
      loadNextChapter(selectedReader.id);
      if (selectedReader && claimChallengeId) {
        loadSuggestions(selectedReader.id, claimChallengeId);
      }
      onLogged?.(null);
    } catch (err) {
      console.error('Failed to undo:', err);
      setError('Could not undo that. Try the Reports tab on My Progress.');
    }
  };

  // The reflection saves into the report's existing notes field.
  const handleSaveReflection = async (reflection) => {
    await Promise.all(
      lastReportIds.map(id => reportApi.updateReport(id, { notes: reflection }))
    );
  };

  if (readers.length === 0) return null;

  return (
    <div className="quick-log">
      <div className="quick-log-top">
        <h2>Log today&apos;s reading</h2>
        {readers.length > 1 && (
          <select
            className="quick-log-reader"
            value={selectedReader?.id || ''}
            onChange={(event) => {
              setSelectedReader(readers.find(r => r.id === event.target.value));
            }}
            aria-label="Who read?"
          >
            {readers.map(reader => (
              <option key={reader.id} value={reader.id}>
                {reader.name}{reader.isPrimary ? ' (You)' : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <p className="quick-log-error">{error}</p>}

      {/* 1. Continue */}
      <button
        type="button"
        className="quick-log-continue"
        onClick={handleContinue}
        disabled={submitting || loadingNext || !nextChapter}
      >
        {loadingNext ? (
          <span className="continue-label">Finding your place…</span>
        ) : nextChapter ? (
          <>
            <span className="continue-label">
              <span className="continue-verb">Read</span>
              <span className="continue-reference">{nextChapter.book} {nextChapter.chapter}</span>
            </span>
            <IoArrowForwardOutline size={20} aria-hidden="true" />
          </>
        ) : (
          <span className="continue-label">Start at Genesis 1</span>
        )}
      </button>

      {/* 2. Free text */}
      <form className="quick-log-text" onSubmit={handleTextSubmit}>
        <label htmlFor="quick-log-input" className="visually-hidden">I read</label>
        <div className="quick-log-input-row">
          <input
            id="quick-log-input"
            type="text"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="I read…  e.g. gen 1-5, john 3"
            autoComplete="off"
            disabled={submitting}
          />
          <button type="submit" disabled={submitting || parsed.chapters.length === 0}>
            Log
          </button>
        </div>

        {chips.length > 0 && (
          <div className="quick-log-chips">
            {chips.map(chip => (
              <span key={chip.label} className="quick-log-chip">{chip.label}</span>
            ))}
            <span className="quick-log-count">
              {parsed.chapters.length} chapter{parsed.chapters.length === 1 ? '' : 's'}
            </span>
          </div>
        )}

        {parsed.errors.length > 0 && text.trim() && (
          <div className="quick-log-hints">
            {parsed.errors.map((message, index) => (
              <p key={index}>{message}</p>
            ))}
          </div>
        )}
      </form>

      {claimsOn && claimEnabled && (
        <ClaimSlot
          enabled
          loading={claimsLoading}
          suggestions={claimSuggestions}
          myClaim={myClaim}
          claiming={claiming || submitting}
          error={claimError}
          onClaim={handleClaim}
          onRelease={handleRelease}
          onLog={handleClaimLog}
        />
      )}

      {/* Adjust: everything the quick path leaves out */}
      <button
        type="button"
        className="quick-log-adjust-toggle"
        onClick={() => setShowAdjust(!showAdjust)}
        aria-expanded={showAdjust}
      >
        Adjust
        {showAdjust ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
      </button>

      {showAdjust && (
        <div className="quick-log-adjust">
          <p>Need a different date, other readers, notes, or reading details?</p>
          <Link to="/report" className="quick-log-adjust-link">
            Log reading details
          </Link>
        </div>
      )}

      {moment && (
        <LogMoment
          moment={moment}
          chapterLabel={momentLabel}
          onClose={() => setMoment(null)}
          onUndo={handleUndo}
          onSaveReflection={lastReportIds.length > 0 ? handleSaveReflection : undefined}
        />
      )}
    </div>
  );
};

QuickLog.propTypes = {
  readers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isPrimary: PropTypes.bool
  })).isRequired,
  userId: PropTypes.string,
  onLogged: PropTypes.func
};

export default QuickLog;
